<script setup lang="ts">
/**
 * Bouton. Hauteur 52 px (40 px en compact), rayon 14 px, comme tous les contrôles.
 * Retour tactile : changement de ton à la pression, sans échelle ni ombre.
 * Pendant `loading`, le bouton reste à sa taille et refuse un second toucher.
 */
import { computed } from 'vue'
import { RouterLink, type RouteLocationRaw } from 'vue-router'

import type { IconName } from '../icons/registry'
import Icon from './Icon.vue'
import Spinner from './Spinner.vue'

const props = withDefaults(
  defineProps<{
    variant?: 'primary' | 'secondary' | 'ghost' | 'danger'
    size?: 'default' | 'compact'
    type?: 'button' | 'submit'
    block?: boolean
    loading?: boolean
    disabled?: boolean
    iconStart?: IconName
    iconEnd?: IconName
    /** Destination : le bouton devient un lien (même apparence). */
    to?: RouteLocationRaw
  }>(),
  {
    variant: 'primary',
    size: 'default',
    type: 'button',
    block: false,
    loading: false,
    disabled: false,
    iconStart: undefined,
    iconEnd: undefined,
    to: undefined,
  },
)

const VARIANTS = {
  primary: 'bg-accent-strong text-ink-inverse active:bg-accent-pressed',
  secondary: 'bg-surface text-ink active:bg-surface-strong',
  ghost: 'bg-bg text-accent-strong active:bg-surface',
  danger: 'bg-danger text-ink-inverse active:bg-danger-pressed',
} as const

const inactive = computed(() => props.disabled || props.loading)

const classes = computed(() => [
  'relative inline-flex select-none items-center justify-center gap-2 rounded-control font-medium transition-colors duration-80 ease-standard',
  props.size === 'default' ? 'h-control px-5 text-body' : 'h-control-compact px-4 text-label',
  props.block ? 'w-full' : '',
  props.disabled ? 'bg-surface text-ink-disabled' : VARIANTS[props.variant],
  inactive.value ? 'cursor-default' : 'cursor-pointer',
])
</script>

<template>
  <RouterLink v-if="to" :to="to" :class="classes">
    <Icon v-if="iconStart" :name="iconStart" />
    <slot />
    <Icon v-if="iconEnd" :name="iconEnd" />
  </RouterLink>
  <button
    v-else
    :type="type"
    :class="classes"
    :disabled="inactive"
    :aria-busy="loading || undefined"
  >
    <span v-if="loading" class="absolute inset-0 flex items-center justify-center">
      <Spinner :size="20" />
    </span>
    <span :class="['inline-flex items-center gap-2', loading ? 'invisible' : '']">
      <Icon v-if="iconStart" :name="iconStart" />
      <slot />
      <Icon v-if="iconEnd" :name="iconEnd" />
    </span>
  </button>
</template>
